import type { TPetDocument } from '~/types/pets';
import { formatDocumentTitle } from '~/utils/petHealth';

export type TPetDocumentPreselectedFile = {
  media_id: number;
  name: string;
  url: string;
  thumbnail?: string | null;
  previewObjectUrl?: string | null;
};

export function resolvePetDocumentMediaId(
  document: Pick<TPetDocument, 'url'> & { mediaId?: number | null },
): number | null {
  if (document.mediaId != null && Number.isFinite(Number(document.mediaId))) {
    return Number(document.mediaId);
  }

  const match = document.url?.match(/\/media\/private\/(\d+)(?:\/|$)/i);
  if (!match) {
    return null;
  }

  const mediaId = Number(match[1]);
  return Number.isFinite(mediaId) ? mediaId : null;
}

export function resolvePetDocumentAbsoluteUrl(
  url: string | null | undefined,
  apiBaseUrl: string,
): string {
  if (!url?.trim()) {
    return '';
  }

  const trimmed = url.trim();
  if (/^(https?:|blob:|data:)/i.test(trimmed)) {
    return trimmed;
  }

  const base = apiBaseUrl.replace(/\/+$/, '');
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;

  if (path.startsWith('/api/') && base.endsWith('/api/v1')) {
    try {
      const origin = new URL(base).origin;
      return `${origin}${path}`;
    } catch {
      return `${base.replace(/\/api\/v1$/, '')}${path}`;
    }
  }

  return `${base}${path}`;
}

export function buildPetDocumentPrivateViewUrl(
  document: Pick<TPetDocument, 'url'> & { mediaId?: number | null },
  apiBaseUrl: string,
): string | null {
  const mediaId = resolvePetDocumentMediaId(document);
  if (!mediaId) {
    return null;
  }

  const base = apiBaseUrl.replace(/\/+$/, '');
  return `${base}/media/private/${mediaId}/view`;
}

export function isPrivatePetDocumentUrl(url: string | null | undefined): boolean {
  return !!url && /\/media\/private\//i.test(url);
}

export function buildPetDocumentPreselectedFile(
  document: TPetDocument,
  apiBaseUrl: string,
): TPetDocumentPreselectedFile {
  const privateViewUrl = buildPetDocumentPrivateViewUrl(document, apiBaseUrl);

  return {
    media_id: resolvePetDocumentMediaId(document) ?? -1,
    name: document.fileName || document.name || formatDocumentTitle(document),
    url: privateViewUrl || resolvePetDocumentAbsoluteUrl(document.url, apiBaseUrl),
  };
}

export function isImageMimeType(mimeType: string | null | undefined): boolean {
  return !!mimeType && mimeType.startsWith('image/');
}

export function isPetDocumentImage(
  document: Pick<TPetDocument, 'url' | 'fileName' | 'name'>,
): boolean {
  const hint = `${document.url || ''} ${document.fileName || ''} ${document.name || ''}`;
  return /\.(jpe?g|png|webp|gif|avif)(\?|#|$)/i.test(hint);
}
